import React from 'react';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faExclamationTriangle,
  faSpinnerThird,
} from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { TYPOGRAPHY } from 'shared/design-system/settings/variables/Typography';
import { ANIMATIONS } from 'shared/design-system/settings/variables/Animation';
import styled from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import { textSelectionNone } from '../base/TrueImpactText';
import TrueImpactLoading from '../../components/TrueImpactLoading';

const BaseButton = styled.button<{
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  backgroundColor?: keyof typeof COLORS;
  backgroundColorHover?: keyof typeof COLORS;
  colorHover?: TrueImpactButtonProps['colorHover'];
}>`
  @keyframes pop {
    0% {
      transform: scale(0);
    }
    80% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
  ${textSelectionNone}
  outline: none;
  border: none;
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  font-family: ${TYPOGRAPHY.fontFamilyBody};
  font-weight: ${TYPOGRAPHY.fontWeightBase};
  font-size: ${TYPOGRAPHY.bodyBase};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  line-height: 1;
  background-color: ${COLORS.ctaPrimary};
  padding: ${SPACERS.small} ${SPACERS.small};
  border-radius: 4px;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'auto')};
  transition: all 200ms ${ANIMATIONS.easing};
  ${({ backgroundColor }) =>
    backgroundColor
      ? `background-color: ${COLORS[backgroundColor]} !important;`
      : ''};
  &:disabled {
    cursor: auto;
    color: ${COLORS.grayBase};
  }
  &:hover {
    ${({ colorHover }) => (colorHover ? `color: ${COLORS[colorHover]};` : '')}
    background-color: ${({ backgroundColorHover }) =>
      COLORS[backgroundColorHover || 'ctaPrimary']};
  }
`;

const ButtonPrimaryLarge = styled(BaseButton)`
  background-color: ${({ backgroundColor }: ButtonProps) =>
    COLORS[backgroundColor || 'ctaPrimary']};
  color: ${({ color }: ButtonProps) => COLORS[color || 'whiteBase']};
  &:disabled {
    color: ${COLORS.grayBase};
    background-color: ${COLORS.grayLight};
  }
`;

const ButtonPrimarySmall = styled(ButtonPrimaryLarge)<ButtonProps>`
  font-size: ${TYPOGRAPHY.bodySmall};
  padding: ${SPACERS.tiny} ${SPACERS.small};
  border-radius: 2px;
`;

const ButtonSecondaryLarge = styled(BaseButton)<ButtonProps>`
  color: ${({ color }) => (color ? COLORS[color] : COLORS.ctaPrimary)};
  outline: 1px solid
    ${({ color }) => (color ? COLORS[color] : COLORS.ctaPrimarySubdued)};
  background-color: unset;
  &:hover {
    background-color: rgba(0, 115, 163, 0.04);
  }
  &:disabled:hover {
    background-color: inherit;
  }
`;

const ButtonSecondaryLargeSuccess = styled(ButtonSecondaryLarge)<ButtonProps>`
  color: ${COLORS.success};
  outline: 1px solid ${COLORS.successSubdued};
  padding: 12px ${SPACERS.small};
`;

const ButtonSecondaryLargeLoading = styled(ButtonSecondaryLarge)`
  color: ${COLORS.blackBase};
  outline: 1px solid ${COLORS.greyBase};
`;

const ButtonSecondaryLargeError = styled(ButtonSecondaryLarge)`
  color: ${COLORS.critical};
  outline: 1px solid ${COLORS.criticalSubdued};
`;

const ButtonSecondarySmall = styled(ButtonSecondaryLarge)`
  font-size: 12px;
  padding: ${SPACERS.tiny} ${SPACERS.tiny};
  border-radius: 2px;
`;

const ButtonSecondarySmallSuccess = styled(ButtonSecondarySmall)`
  color: ${COLORS.success};
  outline: 1px solid ${COLORS.successSubdued};
  padding: 2px ${SPACERS.tiny};
  &:hover {
    background-color: unset;
  }
  svg {
    animation-name: pop;
    animation-duration: 0.3s;
    animation-timing-function: ease-in-out;
    animation-fill-mode: forwards;
    transform: scale(0);
  }
`;

const ButtonSecondarySmallLoading = styled(ButtonSecondarySmall)`
  color: ${COLORS.blackBase};
  outline: 1px solid ${COLORS.greyBase};
  padding: 2px ${SPACERS.tiny};
  &:hover {
    background-color: unset;
  }
`;

const ButtonSecondarySmallError = styled(ButtonSecondarySmall)`
  color: ${COLORS.critical};
  outline: 1px solid ${COLORS.criticalSubdued};
  padding: 2px ${SPACERS.tiny};
  &:hover {
    background-color: unset;
  }
  svg {
    animation-name: pop;
    animation-duration: 0.3s;
    animation-timing-function: ease-in-out;
    animation-fill-mode: forwards;
    transform: scale(0);
  }
`;

const BaseIconOnlyButton = styled.button`
  cursor: pointer;
  min-width: 48px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ backgroundColor }: ButtonProps) =>
    COLORS[backgroundColor || 'ctaPrimary']};
  color: ${({ color }: ButtonProps) => COLORS[color || 'whiteBase']};
  &:hover {
    ${({ colorHover }: ButtonProps) =>
      colorHover ? `color: ${COLORS[colorHover]};` : ''}
    background-color: ${({ backgroundColorHover }: ButtonProps) =>
      COLORS[backgroundColorHover || 'ctaPrimaryHover']};
  }
`;

const ButtonIconOnlyLarge = styled(BaseIconOnlyButton)`
  background-color: ${({ backgroundColor }: ButtonProps) =>
    COLORS[backgroundColor || 'ctaPrimary']};
  color: ${({ color }: ButtonProps) => COLORS[color || 'whiteBase']};
  &:hover {
    ${({ colorHover }: ButtonProps) =>
      colorHover ? `color: ${COLORS[colorHover]};` : ''}
    background-color: ${({ backgroundColorHover }: ButtonProps) =>
      COLORS[backgroundColorHover || 'ctaPrimaryHover']};
  }
`;

const ButtonIconOnlySmall = styled(BaseIconOnlyButton)`
  min-width: 32px;
  width: 32px;
  height: 32px;
  font-size: 16px;
  background-color: ${({ backgroundColor }: ButtonProps) =>
    COLORS[backgroundColor || 'ctaPrimary']};
  color: ${({ color }: ButtonProps) => COLORS[color || 'whiteBase']};
  &:hover {
    ${({ colorHover }: ButtonProps) =>
      colorHover ? `color: ${COLORS[colorHover]};` : ''}
    background-color: ${({ backgroundColorHover }: ButtonProps) =>
      COLORS[backgroundColorHover || 'ctaPrimaryHover']};
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
  font-size: 16px;
`;

export interface TrueImpactButtonProps {
  variant?:
    | 'primary-large'
    | 'primary-small'
    | 'secondary-large'
    | 'secondary-large-success'
    | 'secondary-large-loading'
    | 'secondary-large-error'
    | 'secondary-small'
    | 'secondary-small-success'
    | 'secondary-small-loading'
    | 'secondary-small-error'
    | 'icon-only-large'
    | 'icon-only-small';
  className?: string;
  isLoading?: boolean;
  color?: keyof typeof COLORS;
  colorHover?: keyof typeof COLORS;
  backgroundColor?: keyof typeof COLORS;
  backgroundColorHover?: keyof typeof COLORS;
  disabled?: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode | React.ReactNode[];
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

type ButtonProps = {
  color: TrueImpactButtonProps['color'];
  backgroundColor: TrueImpactButtonProps['backgroundColor'];
  backgroundColorHover: TrueImpactButtonProps['backgroundColorHover'];
  colorHover: TrueImpactButtonProps['colorHover'];
  onClick: TrueImpactButtonProps['onClick'];
};

const TrueImpactButton: React.FC<TrueImpactButtonProps> = ({
  variant = 'primary-large',
  className,
  disabled,
  color,
  colorHover,
  backgroundColor,
  isLoading,
  backgroundColorHover,
  onClick,
  style,
  children,
}) => {
  const handleClick = disabled ? undefined : onClick;
  switch (variant) {
    case 'primary-large':
      return (
        <ButtonPrimaryLarge
          style={style}
          className={`ti-btn-primary-large ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          disabled={isLoading || disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonPrimaryLarge>
      );
    case 'primary-small':
      return (
        <ButtonPrimarySmall
          style={style}
          className={`ti-btn-primary-small ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          disabled={isLoading || disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonPrimarySmall>
      );
    case 'secondary-large':
      return (
        <ButtonSecondaryLarge
          style={style}
          className={`ti-btn-secondary-large ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          disabled={isLoading || disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonSecondaryLarge>
      );
    case 'secondary-large-loading':
      return (
        <ButtonSecondaryLargeLoading
          style={style}
          className={`ti-btn-secondary-large ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          disabled={isLoading || disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          <IconContainer>
            <FontAwesomeIcon icon={faSpinnerThird} />
          </IconContainer>
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonSecondaryLargeLoading>
      );
    case 'secondary-large-error':
      return (
        <ButtonSecondaryLargeError
          style={style}
          className={`ti-btn-secondary-large ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          disabled={isLoading || disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          <IconContainer>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </IconContainer>
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonSecondaryLargeError>
      );
    case 'secondary-large-success':
      return (
        <ButtonSecondaryLargeSuccess
          style={style}
          className={`ti-btn-secondary-large ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          disabled={isLoading || disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          <IconContainer>
            <FontAwesomeIcon icon={faCheck} />
          </IconContainer>
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonSecondaryLargeSuccess>
      );
    case 'secondary-small':
      return (
        <ButtonSecondarySmall
          style={style}
          className={`ti-btn-secondary-small ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          disabled={isLoading || disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonSecondarySmall>
      );
    case 'secondary-small-success':
      return (
        <ButtonSecondarySmallSuccess
          style={style}
          className={`ti-btn-secondary-small ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          disabled={isLoading || disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          <IconContainer>
            <FontAwesomeIcon icon={faCheck} />
          </IconContainer>
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonSecondarySmallSuccess>
      );
    case 'secondary-small-loading':
      return (
        <ButtonSecondarySmallLoading
          style={style}
          className={`ti-btn-secondary-small ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          disabled={isLoading || disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          <IconContainer>
            <FontAwesomeIcon icon={faSpinnerThird} spin />
          </IconContainer>
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonSecondarySmallLoading>
      );
    case 'secondary-small-error':
      return (
        <ButtonSecondarySmallError
          style={style}
          className={`ti-btn-secondary-small ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          disabled={isLoading || disabled}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          <IconContainer>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </IconContainer>
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonSecondarySmallError>
      );
    case 'icon-only-large':
      return (
        <ButtonIconOnlyLarge
          style={style}
          className={`ti-btn-icon-large ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonIconOnlyLarge>
      );
    case 'icon-only-small':
      return (
        <ButtonIconOnlySmall
          style={style}
          className={`ti-btn-icon-small ${className || ''}`}
          onClick={handleClick}
          color={color}
          colorHover={colorHover}
          backgroundColor={backgroundColor}
          backgroundColorHover={backgroundColorHover}
        >
          {isLoading ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={0}
              marginBottom={0}
            />
          ) : (
            children
          )}
        </ButtonIconOnlySmall>
      );
  }
};

interface TrueImpactButtonWithLoadingProps extends TrueImpactButtonProps {
  isLoading?: boolean;
  buttonClassName?: string;
}

const StyledButton = styled.div`
  position: relative;
  button {
    margin-left: auto;
    margin-right: auto;
  }
  .loading-button {
    position: absolute;
    left: 48%;
    top: 10px;
  }
`;

export const TrueImpactButtonWithLoading: React.FC<TrueImpactButtonWithLoadingProps> = ({
  isLoading,
  className,
  buttonClassName,
  ...buttonProps
}) => {
  return (
    <StyledButton className={className}>
      <TrueImpactButton
        {...buttonProps}
        className={buttonClassName}
        disabled={isLoading ? true : buttonProps.disabled}
      />
      {isLoading && <CircularProgress size={30} className="loading-button" />}
    </StyledButton>
  );
};

const StyledTransparentButton = styled.button<{ isText: boolean }>`
  display: flex;
  align-items: center;
  border-radius: 2px;
  cursor: pointer;
  padding: 0 ${SPACERS.tiny};
  color: ${COLORS.ctaPrimary};
  transition: background 0.3s ease-in-out, color 0.3s ease-in-out;
  svg {
    margin-right: ${SPACERS.extraTiny};
  }
  ${({ isText }) =>
    isText
      ? 'border: 1px transparent; background-color: transparent;'
      : `border: 1px solid ${COLORS.ctaPrimary};
  background-color: ${COLORS.whiteBase};
  &:hover {
    color: ${COLORS.whiteBase};
    background-color: ${COLORS.ctaPrimary};
  }
  &:hover svg {
    color: ${COLORS.whiteBase};
  }
  &:active {
    color: ${COLORS.ctaPrimary};
    background-color: ${COLORS.whiteBase};
  }
  &:active svg {
    color: ${COLORS.ctaPrimary};
  }`}
`;

interface TrueImpactTransparentButtonProps {
  children: React.ReactNode | React.ReactNode[];
  icon: FontAwesomeIconProps['icon'];
  isText?: boolean;
  className?: string;
  onClick: () => unknown;
}

export const TrueImpactTransparentButton: React.FC<TrueImpactTransparentButtonProps> = ({
  children,
  className,
  icon,
  isText = false,
  onClick,
}) => {
  return (
    <StyledTransparentButton
      isText={isText}
      onClick={onClick}
      className={className}
    >
      <FontAwesomeIcon icon={icon} />
      {children}
    </StyledTransparentButton>
  );
};

export default TrueImpactButton;
